/**
 * Fiscal Period Context
 *
 * The module's global period and settings, and the period filter's periods: every period with
 * today's dates and the signed-in person's choice per page, all worked out by the server from
 * the business's day (docs/superpowers/specs/2026-10-31-period-filter-design.md). Pages read
 * their period with usePagePeriod(pageKey); a page with no choice follows the global period.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  clearPeriodChoice,
  getGlobalSettings,
  getPeriods,
  savePeriodChoice,
  updateGlobalSettings as updateSettingsAPI
} from '../api/fiscalPeriods';
import { useAuth } from './AuthContext';

const FiscalPeriodContext = createContext(null);

const ALL_TIME = {
  key: 'all_time',
  name: 'All time',
  group: 'Everything',
  start_date: null,
  end_date: null,
  dates: 'All time',
  previous: null
};

const PUBLIC_PATHS = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/demo-login',
  '/sso',
  '/session-ended'
];
// Periods are worked out for a day; after this long away they are fetched again
const STALE_MS = 10 * 60 * 1000;
// Choices the old filter kept in this browser, moved to the account once
const OLD_CHOICE_PREFIX = 'period_filter_';
const OLD_PERIODS = [
  'today',
  'yesterday',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'this_quarter',
  'this_year',
  'all_time'
];

const storage = {
  read(key) {
    try {
      return JSON.parse(localStorage.getItem(key));
    } catch {
      return null;
    }
  },
  write(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // A full or blocked storage only costs the instant first paint
    }
  }
};

const defaults = (module) => ({
  settings: {
    period_type: 'forever',
    period_key: 'all_time',
    custom_start_date: null,
    custom_end_date: null,
    fiscal_year_start_month: 1,
    fiscal_year_start_day: 1,
    allow_users_to_override: true,
    module
  }
});

export const useFiscalPeriod = () => {
  const context = useContext(FiscalPeriodContext);
  if (!context) {
    console.warn('useFiscalPeriod must be used within FiscalPeriodProvider');
    return {
      settings: defaults('retail').settings,
      periodRange: {
        start_date: null,
        end_date: null,
        period_type: 'forever',
        period_label: 'All time'
      },
      periodsData: null,
      loading: false,
      error: null,
      refresh: () => {},
      updateSettings: () => {},
      getDateRangeParams: () => ({}),
      choosePeriod: () => {},
      backToGlobal: () => {}
    };
  }
  return context;
};

/**
 * A page's period: its own choice, or the global period. Pages ask for their figures with
 * period.start_date / period.end_date (both null for all time).
 * @param {string} pageKey - the page's name, e.g. 'sales_list'
 */
export function usePagePeriod(pageKey) {
  const { periodsData, choosePeriod, backToGlobal, module } = useFiscalPeriod();
  const locked = Boolean(periodsData?.locked);
  const saved = !locked && pageKey ? periodsData?.choices?.[pageKey] : null;
  const global = periodsData?.global || ALL_TIME;
  const period = saved || global;
  // The same date parameters pages send their endpoints (all time: no dates, no global period)
  const dateParams = useMemo(
    () =>
      period.start_date || period.end_date
        ? {
            start_date: period.start_date,
            startDate: period.start_date,
            end_date: period.end_date,
            endDate: period.end_date
          }
        : { ignore_global_period: true },
    [period.start_date, period.end_date]
  );
  return {
    ready: Boolean(periodsData),
    module,
    period,
    dateParams,
    isGlobal: !saved,
    locked,
    global,
    periods: periodsData?.periods || [],
    today: periodsData?.today || null,
    choose: (choice) => choosePeriod(pageKey, choice),
    backToGlobal: () => backToGlobal(pageKey)
  };
}

/**
 * A report's "as of" date (balance sheet, aging): its own choice, or the end of the global
 * period (today for all time). date is YYYY-MM-DD.
 * @param {string} pageKey - the report's name, e.g. 'balance_sheet'
 */
export function usePageDate(pageKey) {
  const { periodsData, choosePeriod, backToGlobal, module } = useFiscalPeriod();
  const locked = Boolean(periodsData?.locked);
  const saved = !locked && pageKey ? periodsData?.choices?.[pageKey] : null;
  const today = periodsData?.today || null;
  const global = periodsData?.global_as_of || {
    key: 'global_as_of',
    name: 'Today',
    start_date: today,
    end_date: today,
    dates: ''
  };
  const period = saved || global;
  return {
    ready: Boolean(periodsData),
    module,
    period,
    date: period.end_date,
    isGlobal: !saved,
    locked,
    global,
    options: periodsData?.as_of || [],
    today,
    choose: (choice) => choosePeriod(pageKey, choice),
    backToGlobal: () => backToGlobal(pageKey)
  };
}

export const FiscalPeriodProvider = ({ children, module = 'retail' }) => {
  const { user } = useAuth();
  const cacheKey = user?.id ? `periods_${module}_${user.id}` : null;

  const [settings, setSettings] = useState(defaults(module).settings);
  // Last known periods for this person, so a page shows its period before the server answers
  const [periodsData, setPeriodsData] = useState(() => (cacheKey ? storage.read(cacheKey) : null));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadedAt = useRef(0);

  const remember = useCallback(
    (next) => {
      setPeriodsData(next);
      if (cacheKey && next) storage.write(cacheKey, next);
    },
    [cacheKey]
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [settingsRes, periodsRes] = await Promise.all([
        getGlobalSettings(module),
        getPeriods(module)
      ]);
      if (settingsRes.success) setSettings(settingsRes.data);
      if (periodsRes.success) {
        remember(periodsRes.data);
        loadedAt.current = Date.now();
      }
    } catch (err) {
      console.error('Error fetching fiscal period data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [module, remember]);

  // Load for each signed-in person; public pages and signed-out visitors keep the defaults
  useEffect(() => {
    const isPublicPage = PUBLIC_PATHS.some((path) => window.location.pathname.startsWith(path));
    if (!isPublicPage && user?.id && localStorage.getItem('accessToken')) {
      setPeriodsData(cacheKey ? storage.read(cacheKey) : null);
      fetchData();
    } else {
      setLoading(false);
    }
  }, [module, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Back after a while (possibly a new day): fetch the periods again
  useEffect(() => {
    const onFocus = () => {
      if (loadedAt.current && Date.now() - loadedAt.current > STALE_MS) fetchData();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchData]);

  // Choices the old filter saved in this browser move to the account once
  const migrated = useRef(false);
  useEffect(() => {
    if (migrated.current || !periodsData || periodsData.locked || !loadedAt.current) return;
    migrated.current = true;
    // Wholesale pages' old names: wholesale_*, and the company and distributor pages'
    const ours = (pageKey) =>
      (module === 'wholesale') === /^(wholesale|mfg_|distributor_)/.test(pageKey);
    Object.keys(localStorage)
      .filter((key) => key.startsWith(OLD_CHOICE_PREFIX))
      .forEach((key) => {
        const pageKey = key.slice(OLD_CHOICE_PREFIX.length);
        if (!ours(pageKey)) return;
        const old = storage.read(key) || {};
        let choice = null;
        if (OLD_PERIODS.includes(old.period)) choice = { period_key: old.period };
        else if (old.period === 'custom' && old.customStart && old.customEnd) {
          choice = { start_date: old.customStart, end_date: old.customEnd };
        }
        const done = () => localStorage.removeItem(key);
        if (!choice || periodsData.choices?.[pageKey] || !/^[a-z0-9_]{1,80}$/.test(pageKey))
          return done();
        savePeriodChoice(module, pageKey, choice)
          .then((res) => {
            if (res.success) {
              setPeriodsData((current) => {
                const next = { ...current, choices: { ...current.choices, [pageKey]: res.data } };
                if (cacheKey) storage.write(cacheKey, next);
                return next;
              });
            }
          })
          .catch(() => {})
          .finally(done);
      });
  }, [periodsData, module, cacheKey]);

  /** A page's own period: { period_key } or { start_date, end_date } */
  const choosePeriod = useCallback(
    async (pageKey, choice) => {
      if (!pageKey || !periodsData || periodsData.locked) return;
      const before = periodsData;
      // A period on offer shows at once; own dates as soon as the server has named them
      const listed =
        choice.period_key &&
        [...periodsData.periods, ...(periodsData.as_of || [])].find(
          (p) => p.key === choice.period_key
        );
      const shown = listed || {
        key: 'custom',
        name: 'Your dates',
        group: 'Your dates',
        start_date: choice.start_date,
        end_date: choice.end_date,
        dates: '',
        previous: null
      };
      remember({ ...periodsData, choices: { ...periodsData.choices, [pageKey]: shown } });
      try {
        const res = await savePeriodChoice(module, pageKey, choice);
        if (!res.success) throw new Error(res.message);
        setPeriodsData((current) => {
          const next = { ...current, choices: { ...current.choices, [pageKey]: res.data } };
          if (cacheKey) storage.write(cacheKey, next);
          return next;
        });
      } catch (err) {
        console.error('Saving the period failed:', err);
        remember(before);
        fetchData();
      }
    },
    [periodsData, module, remember, cacheKey, fetchData]
  );

  /** The page goes back to the global period */
  const backToGlobal = useCallback(
    async (pageKey) => {
      if (!pageKey || !periodsData?.choices?.[pageKey]) return;
      const before = periodsData;
      const choices = { ...periodsData.choices };
      delete choices[pageKey];
      remember({ ...periodsData, choices });
      try {
        await clearPeriodChoice(module, pageKey);
      } catch (err) {
        console.error('Going back to the global period failed:', err);
        remember(before);
      }
    },
    [periodsData, module, remember]
  );

  // Update the module's settings (the settings page), then its periods
  const updateSettings = async (newSettings) => {
    try {
      setLoading(true);
      const response = await updateSettingsAPI(newSettings, module);
      if (response.success) {
        setSettings(response.data);
        const periodsRes = await getPeriods(module);
        if (periodsRes.success) remember(periodsRes.data);
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (err) {
      console.error('Error updating fiscal settings:', err);
      return { success: false, message: err.response?.data?.message || err.message };
    } finally {
      setLoading(false);
    }
  };

  // The global period in the shape pages used before the period filter (start/end, type, label)
  const global = periodsData?.global || ALL_TIME;
  const periodRange = useMemo(
    () => ({
      start_date: global.start_date,
      end_date: global.end_date,
      period_type: global.key === 'all_time' ? 'forever' : global.key,
      period_key: global.key,
      period_label: global.name
    }),
    [global]
  );

  // Date range params for API calls (the global period unless a range is given)
  const getDateRangeParams = useCallback(
    (overrideRange = null) => {
      const range = overrideRange || periodRange;
      if (range.period_type === 'forever' || (!range.start_date && !range.end_date)) {
        return { ignore_global_period: true };
      }
      const params = {};
      if (range.start_date) {
        params.start_date = range.start_date;
        params.startDate = range.start_date; // Some endpoints use camelCase
      }
      if (range.end_date) {
        params.end_date = range.end_date;
        params.endDate = range.end_date;
      }
      return params;
    },
    [periodRange]
  );

  const value = {
    settings,
    periodRange,
    periodsData,
    loading,
    error,
    refresh: fetchData,
    updateSettings,
    getDateRangeParams,
    choosePeriod,
    backToGlobal,
    // Convenience getters
    isAllTime: global.key === 'all_time',
    periodLabel: global.name,
    startDate: global.start_date,
    endDate: global.end_date,
    module
  };

  return <FiscalPeriodContext.Provider value={value}>{children}</FiscalPeriodContext.Provider>;
};

export default FiscalPeriodContext;
